import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './CabinetsSelectPage.css';

function SidebarIcon({ name }) {
  const common = {
    viewBox: '0 0 24 24',
    fill: 'none',
    xmlns: 'http://www.w3.org/2000/svg',
    'aria-hidden': true,
    className: 'cabinets-sidebar-svg',
  };
  switch (name) {
    case 'building':
      return (
        <svg {...common}>
          <path
            d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z"
            fill="currentColor"
          />
        </svg>
      );
    case 'key':
      return (
        <svg {...common}>
          <path
            d="M12.65 10C11.83 7.67 9.61 6 7 6c-3.31 0-6 2.69-6 6s2.69 6 6 6c2.61 0 4.83-1.67 5.65-4H17v4h4v-4h2v-4H12.65zM7 14c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z"
            fill="currentColor"
          />
        </svg>
      );
    case 'users':
      return (
        <svg {...common}>
          <path
            d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5s-3 1.34-3 3 1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5C15 14.17 10.33 13 8 13zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"
            fill="currentColor"
          />
        </svg>
      );
    case 'user-manage':
      return (
        <svg {...common}>
          <path
            d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
            fill="currentColor"
          />
        </svg>
      );
    case 'logout':
      return (
        <svg {...common}>
          <path
            d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5-5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"
            fill="currentColor"
          />
        </svg>
      );
    case 'menu':
      return (
        <svg {...common}>
          <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" fill="currentColor" />
        </svg>
      );
    case 'close':
      return (
        <svg {...common}>
          <path
            d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z"
            fill="currentColor"
          />
        </svg>
      );
    case 'chevron':
      return (
        <svg {...common}>
          <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z" fill="currentColor" />
        </svg>
      );
    default:
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="8" fill="currentColor" />
        </svg>
      );
  }
}

/**
 * Shell admin multi-cabinets : sidebar + zone principale.
 * Prop optionnelle onAjouterCabinet pour ouvrir le formulaire sur /cabinets.
 */
export default function CabinetsAdminShell({
  children,
  onAjouterCabinet,
  activeNav: activeNavProp,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const path = location.pathname;
  const cabinetsSectionActive =
    path === '/cabinets' || path.startsWith('/cabinets/acces');
  const gererCabinetActive = path === '/cabinets';
  const pointAccesActive = path.startsWith('/cabinets/acces');

  const clientsSectionActive = path.startsWith('/cabinets/clients');
  const clientsActifsActive =
    path === '/cabinets/clients' || path.startsWith('/cabinets/clients/actifs');
  const clientsInactifsActive = path.startsWith('/cabinets/clients/inactifs');
  const usersSectionActive =
    path === '/cabinets/users' || path.startsWith('/cabinets/users/');

  const activeNav =
    activeNavProp ||
    (usersSectionActive
      ? 'users'
      : clientsSectionActive
        ? 'clients'
        : pointAccesActive
          ? 'acces'
          : gererCabinetActive
            ? 'liste'
            : null);

  const [cabinetsOpen, setCabinetsOpen] = useState(cabinetsSectionActive);
  const [clientsOpen, setClientsOpen] = useState(clientsSectionActive);

  useEffect(() => {
    if (cabinetsSectionActive) setCabinetsOpen(true);
  }, [cabinetsSectionActive]);

  useEffect(() => {
    if (clientsSectionActive) setClientsOpen(true);
  }, [clientsSectionActive]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const closeSidebar = () => setSidebarOpen(false);

  const go = (fn) => {
    closeSidebar();
    fn();
  };

  return (
    <div className="cabinets-select-page cabinets-select-page--with-sidebar">
      <button
        type="button"
        className="cabinets-sidebar-toggle"
        onClick={() => setSidebarOpen((v) => !v)}
        aria-label={sidebarOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
        aria-expanded={sidebarOpen}
      >
        <SidebarIcon name={sidebarOpen ? 'close' : 'menu'} />
      </button>

      {sidebarOpen ? (
        <button
          type="button"
          className="cabinets-sidebar-backdrop"
          aria-label="Fermer le menu"
          onClick={closeSidebar}
        />
      ) : null}

      <aside
        className={`cabinets-admin-sidebar${sidebarOpen ? ' is-open' : ''}`}
        aria-label="Menu admin"
      >
        <div className="cabinets-admin-sidebar__brand">
          <strong>Administration</strong>
        </div>

        <nav className="cabinets-admin-sidebar__nav">
          {/* 1. Cabinets */}
          <div
            className={`cabinets-admin-sidebar__group${cabinetsOpen || cabinetsSectionActive ? ' is-open' : ''}${cabinetsSectionActive ? ' is-section-active' : ''}`}
          >
            <button
              type="button"
              className={`cabinets-admin-sidebar__item${cabinetsSectionActive ? ' is-active' : ''}`}
              onClick={() => {
                const next = !(cabinetsOpen || cabinetsSectionActive);
                setCabinetsOpen(next);
                if (next && !cabinetsSectionActive) {
                  go(() => navigate('/cabinets'));
                }
              }}
              aria-expanded={cabinetsOpen || cabinetsSectionActive}
            >
              <span className="cabinets-admin-sidebar__icon" aria-hidden="true">
                <SidebarIcon name="building" />
              </span>
              <span className="cabinets-admin-sidebar__label">Cabinets</span>
              <span
                className={`cabinets-admin-sidebar__chevron${cabinetsOpen || cabinetsSectionActive ? ' is-open' : ''}`}
                aria-hidden="true"
              >
                <SidebarIcon name="chevron" />
              </span>
            </button>
            {(cabinetsOpen || cabinetsSectionActive) && (
              <div className="cabinets-admin-sidebar__subnav">
                <button
                  type="button"
                  className={`cabinets-admin-sidebar__subitem${gererCabinetActive || activeNav === 'liste' || activeNav === 'ajouter' ? ' is-active' : ''}`}
                  onClick={() =>
                    go(() => {
                      if (typeof onAjouterCabinet === 'function' && activeNav === 'ajouter') {
                        onAjouterCabinet();
                      }
                      navigate('/cabinets');
                    })
                  }
                >
                  Gérer cabinet
                </button>
                <button
                  type="button"
                  className={`cabinets-admin-sidebar__subitem${pointAccesActive || activeNav === 'acces' ? ' is-active' : ''}`}
                  onClick={() => go(() => navigate('/cabinets/acces'))}
                >
                  Point d&apos;accès cabinet
                </button>
              </div>
            )}
          </div>

          {/* 2. Gérer les clients */}
          <div
            className={`cabinets-admin-sidebar__group${clientsOpen || clientsSectionActive ? ' is-open' : ''}${clientsSectionActive ? ' is-section-active' : ''}`}
          >
            <button
              type="button"
              className={`cabinets-admin-sidebar__item${clientsSectionActive ? ' is-active' : ''}`}
              onClick={() => {
                const next = !(clientsOpen || clientsSectionActive);
                setClientsOpen(next);
                if (next && !clientsSectionActive) {
                  go(() => navigate('/cabinets/clients/actifs'));
                }
              }}
              aria-expanded={clientsOpen || clientsSectionActive}
            >
              <span className="cabinets-admin-sidebar__icon" aria-hidden="true">
                <SidebarIcon name="users" />
              </span>
              <span className="cabinets-admin-sidebar__label">Gérer les clients</span>
              <span
                className={`cabinets-admin-sidebar__chevron${clientsOpen || clientsSectionActive ? ' is-open' : ''}`}
                aria-hidden="true"
              >
                <SidebarIcon name="chevron" />
              </span>
            </button>
            {(clientsOpen || clientsSectionActive) && (
              <div className="cabinets-admin-sidebar__subnav" data-clients-menu="actifs-inactifs">
                <button
                  type="button"
                  className={`cabinets-admin-sidebar__subitem${clientsActifsActive ? ' is-active' : ''}`}
                  onClick={() => go(() => navigate('/cabinets/clients/actifs'))}
                >
                  Clients actifs
                </button>
                <button
                  type="button"
                  className={`cabinets-admin-sidebar__subitem${clientsInactifsActive ? ' is-active' : ''}`}
                  onClick={() => go(() => navigate('/cabinets/clients/inactifs'))}
                >
                  Clients inactifs
                </button>
              </div>
            )}
          </div>

          {/* 3. Gérer users */}
          <button
            type="button"
            className={`cabinets-admin-sidebar__item${usersSectionActive || activeNav === 'users' ? ' is-active' : ''}`}
            onClick={() => go(() => navigate('/cabinets/users'))}
          >
            <span className="cabinets-admin-sidebar__icon" aria-hidden="true">
              <SidebarIcon name="user-manage" />
            </span>
            <span className="cabinets-admin-sidebar__label">Gérer users</span>
          </button>
        </nav>

        <div className="cabinets-admin-sidebar__bottom">
          <button
            type="button"
            className="cabinets-admin-sidebar__item cabinets-admin-sidebar__item--logout"
            onClick={handleLogout}
          >
            <span className="cabinets-admin-sidebar__icon" aria-hidden="true">
              <SidebarIcon name="logout" />
            </span>
            <span>Déconnexion</span>
          </button>
        </div>
      </aside>

      <div className="cabinets-select-main">{children}</div>
    </div>
  );
}
